import { useCallback, useEffect, useMemo } from "react";
import { Option, Product, SortOrder } from "tm-shared/types";

const compareValues = (a: unknown, b: unknown): number => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    if (typeof a === "number" && typeof b === "number") return a - b;
    if (typeof a === "boolean" && typeof b === "boolean")
        return Number(a) - Number(b);

    return String(a)
        .toLowerCase()
        .localeCompare(String(b).toLowerCase(), undefined, { numeric: true });
};

type ParamKey = keyof Product["params"];

interface UseProductsSortProps {
    items: Product[];
    excludeTags?: ParamKey[];
    tag: string | null;
    order: SortOrder;
    onTagChange?: (tag: string | null) => void;
}

export const useProductsSort = ({
    items,
    excludeTags = ["image"],
    tag,
    order,
    onTagChange,
}: UseProductsSortProps) => {
    const options: Option[] = useMemo(() => {
        if (!items.length) return [];

        const common = new Map<string, string>();
        for (const p of Object.values(items[0].params)) {
            common.set(p.paramTag, p.paramName);
        }

        for (let i = 1; i < items.length; i++) {
            const present = new Set(
                Object.values(items[i].params).map((p) => p.paramTag),
            );

            for (const tag of Array.from(common.keys())) {
                if (!present.has(tag)) common.delete(tag);
            }
        }

        if (excludeTags?.length) {
            for (const tag of excludeTags) common.delete(tag);
        }

        return Array.from(common.entries()).map(([value, label]) => ({
            value,
            label,
        }));
    }, [items, excludeTags]);

    useEffect(() => {
        if (tag && options.length && !options.some((o) => o.value === tag)) {
            onTagChange?.(null);
        }
    }, [options, tag, onTagChange]);

    const getValue = useCallback(
        (item: Product) => {
            if (!tag) return null;
            return item.params?.[tag]?.value ?? null;
        },
        [tag],
    );

    const sorted = useMemo(() => {
        if (!tag) return items;
        const arr = items.map((it, i) => ({ it, i }));
        arr.sort((a, b) => {
            const va = getValue(a.it);
            const vb = getValue(b.it);
            let cmp = compareValues(va, vb);
            if (order === SortOrder.Desc) cmp = -cmp;
            return cmp || a.i - b.i;
        });
        return arr.map((x) => x.it);
    }, [items, tag, order, getValue]);

    return {
        options,
        value: tag,
        order,
        sorted,
    };
};
